import { useEffect, useState } from 'react';
import { LithoPatternDefs, lithoFill } from './lithoPatterns';
import type { BoreholeCardModel, CardLayer } from './cardTypes';
import { formatCoordinates } from '@/lib/coordFormat';

// Elementy podglądu można klikać, by przejść do odpowiedniego pola w edytorze — do każdej klikalnej
// grupy dodajemy `data-field` z identyfikatorem pola (parsowanym przez rodzica). Indeksy w kolekcjach
// (`_idx`) są ORYGINALNE (przed sortowaniem/filtrowaniem), żeby pasowały do wierszy formularza.
type IdxLayer = CardLayer & { _idx: number };
type IdxCasing = BoreholeCardModel['casing'][number] & { _idx: number };
type IdxLevel = BoreholeCardModel['waterLevels'][number] & { _idx: number };
type IdxLiq = BoreholeCardModel['liquidation'][number] & { _idx: number };

// „Karta otworu" (Zestawienie wyników wiercenia otworu) jako jednostronicowy SVG A4 (rodzina MODERN).
// Ten sam SVG jest źródłem podglądu w edytorze i wektorowego PDF (Apache FOP) — bez zewnętrznego CSS,
// wszystko przez atrybuty prezentacji, żeby transkoder Batik/FOP renderował 1:1.

const W = 794;   // A4 @96dpi
const H = 1123;
const M = 10;     // margines ramki
const FONT = 'DejaVu Sans, Arial, sans-serif';

// ---------- formatery ----------
const pl = (n: number | null | undefined, dp = 2): string =>
  n === null || n === undefined ? '' : n.toLocaleString('pl-PL', { minimumFractionDigits: 0, maximumFractionDigits: dp });
const plFixed = (n: number | null | undefined, dp = 2): string =>
  n === null || n === undefined ? '' : n.toLocaleString('pl-PL', { minimumFractionDigits: dp, maximumFractionDigits: dp });
const fmtDate = (iso: string | null | undefined): string => {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return d && m && y ? `${d}.${m}.${y}` : iso;
};
const nz = (s: string | null | undefined): string => (s && s.trim() ? s : '');

/** Prosty word-wrap do tspanów wg szacowanej szerokości znaku. */
function wrap(text: string, maxW: number, fontSize: number): string[] {
  if (!text) return [];
  const cw = fontSize * 0.52;
  const max = Math.max(1, Math.floor(maxW / cw));
  const words = text.split(/\s+/);
  const lines: string[] = [];
  let line = '';
  for (const w of words) {
    const cand = line ? line + ' ' + w : w;
    if (cand.length <= max) line = cand;
    else { if (line) lines.push(line); line = w.length > max ? w.slice(0, max) : w; }
  }
  if (line) lines.push(line);
  return lines;
}

// Zestawy kolumn per rodzina/typ. „left"=suma szerokości lewej części (MODERN: 530, reszta: pełna 774).
type Col = { key: string; w: number };
const MODERN_COLS: Col[] = [
  { key: 'depth', w: 30 }, { key: 'casing', w: 96 }, { key: 'water', w: 24 }, { key: 'litho', w: 38 },
  { key: 'depthNo', w: 32 }, { key: 'desc', w: 150 }, { key: 'strat', w: 40 }, { key: 'soil', w: 24 },
  { key: 'tools', w: 48 }, { key: 'course', w: 48 },
];
const ZZWW_COLS: Col[] = [
  { key: 'depth', w: 26 }, { key: 'casing', w: 80 }, { key: 'water', w: 22 }, { key: 'litho', w: 34 },
  { key: 'depthNo', w: 28 }, { key: 'desc', w: 130 }, { key: 'strat', w: 32 }, { key: 'soil', w: 22 },
  { key: 'tools', w: 40 }, { key: 'course', w: 40 }, { key: 'results', w: 260 }, { key: 'uwagi', w: 60 },
];
// PROJEKT geologiczno-techniczny (PGTO): część geologiczna + techniczna (konstrukcja projektowana + parametry wiercenia).
const PROJEKT_COLS: Col[] = [
  { key: 'depth', w: 26 }, { key: 'strat', w: 30 }, { key: 'litho', w: 34 }, { key: 'depthNo', w: 28 },
  { key: 'desc', w: 150 }, { key: 'water', w: 24 }, { key: 'difficulties', w: 40 }, { key: 'plannedTests', w: 96 },
  { key: 'casing', w: 96 }, { key: 'mud', w: 40 }, { key: 'bit', w: 44 }, { key: 'pressure', w: 26 },
  { key: 'rpm', w: 26 }, { key: 'flow', w: 30 }, { key: 'uwagi', w: 84 },
];

// Likwidacja / rekonstrukcja: dwie kolumny konstrukcji (stan istniejący + projekt).
const LIKREK_COLS: Col[] = [
  { key: 'depth', w: 26 }, { key: 'strat', w: 28 }, { key: 'litho', w: 34 }, { key: 'depthNo', w: 28 },
  { key: 'desc', w: 150 }, { key: 'water', w: 24 }, { key: 'casingExisting', w: 172 },
  { key: 'casingPlanned', w: 172 }, { key: 'uwagi', w: 90 },
];

function defaultCardTitle(type: string, isZzww: boolean, desig: string): string {
  switch (type) {
    case 'PROJEKT_NOWA': return `PROJEKT GEOLOGICZNO-TECHNICZNY OTWORU NR ${desig}`;
    case 'PROJEKT_LIKWIDACJA': return `PROJEKT GEOLOGICZNO-TECHNICZNY LIKWIDACJI OTWORU NR ${desig}`;
    case 'PROJEKT_REKONSTRUKCJA': return `PROJEKT GEOLOGICZNO-TECHNICZNY REKONSTRUKCJI OTWORU NR ${desig}`;
    case 'DOKUMENTACJA_LIKWIDACJI': return `DOKUMENTACJA LIKWIDACJI OTWORU NR ${desig}`;
    default: return isZzww
      ? `ZBIORCZE ZESTAWIENIE WYNIKÓW WIERCENIA OTWORU NR ${desig}`
      : `ZESTAWIENIE WYNIKÓW WIERCENIA OTWORU HYDROGEOLOGICZNEGO NR ${desig}`;
  }
}

interface Props {
  model: BoreholeCardModel;
  /** Miniatura mapy jako data URI (PNG); gdy brak — placeholder. */
  mapImage?: string | null;
  /** Logo firmy jako data URI; gdy brak lub showLogo=false — pomijane. */
  logoImage?: string | null;
}

export function BoreholeCardSvg({ model, mapImage, logoImage }: Props) {
  const p = model.presentation;
  const c = model.context;

  // Proporcje logo mierzone z samego pliku. `preserveAspectRatio` nie ratuje SVG bez `viewBox`
  // (a takie bywają logotypy firm) — przeglądarka rozciąga je na całe pole i logo wychodzi
  // zniekształcone. Ze zmierzonym stosunkiem boków pole ma dokładnie proporcje pliku, więc
  // skalowanie jest neutralne także w PDF (eksport serializuje ten sam, już zmierzony SVG).
  const [logoAspect, setLogoAspect] = useState<number | null>(null);
  useEffect(() => {
    if (!logoImage) { setLogoAspect(null); return; }
    let alive = true;
    const probe = new Image();
    probe.onload = () => {
      if (alive && probe.naturalWidth > 0 && probe.naturalHeight > 0) {
        setLogoAspect(probe.naturalWidth / probe.naturalHeight);
      }
    };
    probe.onerror = () => { if (alive) setLogoAspect(null); };
    probe.src = logoImage;
    return () => { alive = false; };
  }, [logoImage]);

  const layers: IdxLayer[] = model.layers
    .map((l, _idx) => ({ ...l, _idx }))
    .filter((l) => l.fromDepth != null && l.toDepth != null)
    .sort((a, b) => (a.fromDepth ?? 0) - (b.fromDepth ?? 0));

  // kolekcje z zachowanym oryginalnym indeksem (do klikalnych `data-field` → wiersz formularza)
  const casingIdx: IdxCasing[] = model.casing.map((s, _idx) => ({ ...s, _idx }));
  const levelsIdx: IdxLevel[] = model.waterLevels.map((lv, _idx) => ({ ...lv, _idx }));
  const liquidationIdx: IdxLiq[] = model.liquidation.map((z, _idx) => ({ ...z, _idx }));

  const maxDepth = Math.max(
    model.totalDepth ?? 0,
    ...layers.map((l) => l.toDepth ?? 0),
    ...model.casing.map((s) => s.toDepth ?? 0),
    1,
  );

  const type = p.cardType ?? 'DOKUMENTACJA_WYKONANIA';
  const isProjekt = type.startsWith('PROJEKT');
  const isLikRek = type === 'PROJEKT_LIKWIDACJA' || type === 'PROJEKT_REKONSTRUKCJA' || type === 'DOKUMENTACJA_LIKWIDACJI';
  const isLikwidacja = type === 'PROJEKT_LIKWIDACJA' || type === 'DOKUMENTACJA_LIKWIDACJI';
  const isZzww = type === 'DOKUMENTACJA_WYKONANIA' && p.templateFamily === 'ZZWW';
  const title = p.titleVariant || defaultCardTitle(type, isZzww, nz(model.designation));

  // ---------- geometria ----------
  // Pasek tytułu mieści tytuł, a przy włączonym logo także logo firmy — w CAŁOŚCI nad kreską
  // oddzielającą nagłówek (logo o stałej wysokości 30 px wychodziło poza pasek i kreska je przecinała).
  // Bez logo pasek ma dotychczasową wysokość, więc geometria takich kart się nie zmienia.
  const showLogo = p.showLogo !== false && !!logoImage;
  const LOGO_W = 100, LOGO_H = 26, LOGO_PAD = 3;   // maksymalne pole na logo (wysokość ustala pasek)
  const TITLE_H = showLogo ? LOGO_H + 2 * LOGO_PAD : 24;
  const TITLE_RULE = M + TITLE_H;                  // kreska zamykająca pasek tytułu
  // logo wpisane w pole z zachowaniem proporcji: dosunięte do prawej ramki, wyśrodkowane w pionie
  const logoAr = logoAspect && logoAspect > 0 ? logoAspect : LOGO_W / LOGO_H;
  const logoW = Math.min(LOGO_W, LOGO_H * logoAr);
  const logoH = Math.min(LOGO_H, LOGO_W / logoAr);
  const LOGO_X = W - M - LOGO_PAD - logoW;
  const LOGO_Y = M + LOGO_PAD + (LOGO_H - logoH) / 2;
  // Tytuł wyśrodkowany w wolnej części paska (na lewo od logo) i zmniejszany, gdyby nie mieścił się
  // w szerokości — inaczej długi tytuł wchodził pod logo (≈0,64 em na znak dla wersalików półgrubych).
  const titleRight = showLogo ? LOGO_X - 8 : W - M;
  const titleFs = Math.max(8.5, Math.min(13, (titleRight - M - 8) / Math.max(1, title.length * 0.64)));

  const HEADER_TOP = TITLE_RULE + 2;
  const MAP_W = 252, MAP_H = 168;
  const HEADER_BOT = HEADER_TOP + MAP_H + 78;      // pod mapą jest pas obliczeń
  const COLHEAD_H = 60;
  const PROF_TOP = HEADER_BOT + COLHEAD_H;
  const TITLE_BLOCK_H = p.showTitleBlock ? 66 : 0;   // metryczka na dole
  const PROF_BOT = H - 20 - TITLE_BLOCK_H;
  // Wystawka rury ponad terenem → rezerwujemy pasek nad linią „0,0" (wspólny dla wszystkich kolumn,
  // więc głęb. 0 = teren pozostaje spójne; tylko konstrukcja rysuje w nim wystającą rurę).
  const stickup = p.casingStickupM ?? 0;
  const STICKUP_BAND = stickup > 0 ? 14 : 0;
  const GROUND_TOP = PROF_TOP + STICKUP_BAND;
  const PROF_H = PROF_BOT - GROUND_TOP;
  const scale = PROF_H / maxDepth;
  const yAt = (d: number) => GROUND_TOP + d * scale;

  // kolumny: zestaw wg typu karty, filtr wg hiddenColumns, normalizacja szerokości do dostępnego obszaru
  const hidden = new Set(p.hiddenColumns ?? []);
  const hasPanel = type === 'DOKUMENTACJA_WYKONANIA' && !isZzww && !hidden.has('waterpanel');
  const base = (isLikRek ? LIKREK_COLS : type === 'PROJEKT_NOWA' ? PROJEKT_COLS : isZzww ? ZZWW_COLS : MODERN_COLS)
    .filter((cc) => !hidden.has(cc.key));
  const targetW = hasPanel ? 530 : (W - 2 * M);
  const rawSum = base.reduce((s, cc) => s + cc.w, 0) || 1;
  const cols = base.map((cc) => ({ key: cc.key, w: cc.w * targetW / rawSum }));
  const x: Record<string, number> = {};
  let cx = M;
  for (const col of cols) { x[col.key] = cx; cx += col.w; }
  const PANEL_X = hasPanel ? cx : (W - M);
  const colX = (k: string) => x[k];
  const colW = (k: string) => cols.find((cc) => cc.key === k)?.w ?? 0;
  const has = (k: string) => x[k] !== undefined;

  // kolumny tekstowe (renderowane pionowo z pól modelu / karty)
  const textCols: Record<string, string> = {
    tools: nz(model.drillingTools), course: nz(model.drillingCourse),
    mud: nz(p.mudType), bit: nz(p.bitType), pressure: nz(p.drillPressure),
    rpm: nz(p.drillRpm), flow: nz(p.mudFlow), plannedTests: nz(p.plannedTests),
    uwagi: nz(p.supervisionNotes),
  };

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      width="210mm"
      height="297mm"
      xmlns="http://www.w3.org/2000/svg"
      xmlnsXlink="http://www.w3.org/1999/xlink"
      style={{ background: '#fff', fontFamily: FONT }}
    >
      <defs><LithoPatternDefs /></defs>
      <rect x={M} y={M} width={W - 2 * M} height={H - 2 * M} fill="#fff" stroke="#000" strokeWidth={1} />

      {/* ---------- TYTUŁ + LOGO FIRMY ---------- */}
      <text x={(M + titleRight) / 2} y={M + TITLE_H / 2 + titleFs * 0.35} textAnchor="middle"
        fontFamily={FONT} fontSize={titleFs} fontWeight="bold" data-field="pres.titleVariant">
        {title}
      </text>
      {showLogo && logoImage && (
        <image x={LOGO_X} y={LOGO_Y} width={logoW} height={logoH}
          href={logoImage} xlinkHref={logoImage} preserveAspectRatio="xMaxYMid meet" data-field="pres.company" />
      )}
      <line x1={M} y1={TITLE_RULE} x2={W - M} y2={TITLE_RULE} stroke="#000" strokeWidth={0.6} />

      {/* ---------- NAGŁÓWEK ---------- */}
      <Header
        model={model} c={c} p={p} mapImage={mapImage}
        top={HEADER_TOP} mapW={MAP_W} mapH={MAP_H} bottom={HEADER_BOT}
      />

      {/* ---------- GŁÓWKI KOLUMN ---------- */}
      <ColumnHeaders cols={cols} x={x} top={HEADER_BOT} height={COLHEAD_H} panelX={PANEL_X} />

      {/* ---------- SIATKA PROFILU ---------- */}
      {cols.map((col) => (
        <line key={'v' + col.key} x1={colX(col.key)} y1={HEADER_BOT} x2={colX(col.key)} y2={PROF_BOT}
          stroke="#000" strokeWidth={0.5} />
      ))}
      {hasPanel && <line x1={PANEL_X} y1={HEADER_BOT} x2={PANEL_X} y2={PROF_BOT} stroke="#000" strokeWidth={0.8} />}
      <line x1={M} y1={PROF_BOT} x2={W - M} y2={PROF_BOT} stroke="#000" strokeWidth={0.6} />

      {/* ---------- SKALA GŁĘBOKOŚCI ---------- */}
      {has('depth') && <DepthAxis x={colX('depth')} w={colW('depth')} maxDepth={maxDepth} yAt={yAt} />}

      {/* ---------- LITOLOGIA + GŁĘB. + OPIS + KAT. GRUNTU ---------- */}
      {has('litho') && layers.map((l, i) => (
        <LayerRow key={l.id ?? 'l' + i} l={l} yAt={yAt}
          xLitho={colX('litho')} wLitho={colW('litho')}
          xDepthNo={colX('depthNo')} wDepthNo={colW('depthNo')}
          xDesc={colX('desc')} wDesc={colW('desc')}
          xSoil={has('soil') ? colX('soil') : -9999} wSoil={colW('soil')} />
      ))}
      {has('strat') && <StratigraphyColumn layers={layers} yAt={yAt} x={colX('strat')} w={colW('strat')} />}

      {/* ---------- KONSTRUKCJA (pojedyncza / dwustanowa dla likwidacji/rekonstrukcji) ---------- */}
      {has('casing') && <CasingColumn casing={casingIdx} yAt={yAt} x={colX('casing')} w={colW('casing')} stickup={stickup} />}
      {has('casingExisting') && <CasingColumn casing={casingIdx.filter((s) => s.phase === 'EXISTING')} yAt={yAt} x={colX('casingExisting')} w={colW('casingExisting')} showDecision />}
      {has('casingPlanned') && (isLikwidacja
        ? <LiquidationColumn zones={liquidationIdx} yAt={yAt} x={colX('casingPlanned')} w={colW('casingPlanned')} />
        : <CasingColumn casing={casingIdx.filter((s) => s.phase === 'PLANNED')} yAt={yAt} x={colX('casingPlanned')} w={colW('casingPlanned')} stickup={stickup} />)}

      {/* ---------- ZWIERCIADŁO WODY ---------- */}
      {has('water') && <WaterLevels levels={levelsIdx} yAt={yAt} x={colX('water')} w={colW('water')} />}

      {/* ---------- KOLUMNY TEKSTOWE (narzędzia/przebieg/parametry wiercenia — pionowo) ---------- */}
      {cols.filter((col) => textCols[col.key]).map((col) => (
        <VerticalText key={'t' + col.key} text={textCols[col.key]} x={colX(col.key) + colW(col.key) / 2}
          yTop={PROF_TOP + 6} yBot={PROF_BOT - 6} fontSize={6.5} />
      ))}

      {/* ---------- WYNIKI BADANIA WODY: panel (MODERN) / kolumna 11 (ZZWW) ---------- */}
      {has('results') && <WaterColumn model={model} x={colX('results')} w={colW('results')} top={PROF_TOP} bottom={PROF_BOT} />}
      {hasPanel && <WaterPanel model={model} x={PANEL_X} right={W - M} top={HEADER_BOT} bodyTop={PROF_TOP} bottom={PROF_BOT} />}

      {/* ---------- STOPKA: nr załącznika (pionowo) ---------- */}
      {nz(p.attachmentLabel) && (
        <text x={W - M - 6} y={PROF_BOT - 6} fontFamily={FONT} fontSize={11} fontWeight="bold"
          textAnchor="start" transform={`rotate(-90 ${W - M - 6} ${PROF_BOT - 6})`} data-field="pres.attachmentLabel">
          {p.attachmentLabel}
        </text>
      )}

      {/* ---------- METRYCZKA RYSUNKU (tabela rewizji + podpisy) ---------- */}
      {p.showTitleBlock && (
        <g data-field="titleblock">
          <TitleBlock model={model} c={c} p={p} title={title} top={PROF_BOT + 2} bottom={H - M} />
        </g>
      )}
    </svg>
  );
}

// ==================== METRYCZKA RYSUNKU ====================
function TitleBlock({ model, c, p, title, top, bottom }: {
  model: BoreholeCardModel; c: BoreholeCardModel['context']; p: BoreholeCardModel['presentation'];
  title: string; top: number; bottom: number;
}) {
  const h = bottom - top;
  const splitX = M + (W - 2 * M) * 0.52;   // lewa część = rewizje, prawa = tabliczka
  const revs = model.revisions ?? [];
  return (
    <g>
      <rect x={M} y={top} width={W - 2 * M} height={h} fill="#fff" stroke="#000" strokeWidth={0.7} />
      <line x1={splitX} y1={top} x2={splitX} y2={bottom} stroke="#000" strokeWidth={0.5} />

      {/* lewa: tabela rewizji */}
      <text x={M + 4} y={top + 9} fontFamily={FONT} fontSize={6.5} fontWeight="bold">Rewizje</text>
      <text x={M + 6} y={top + 18} fontFamily={FONT} fontSize={6} fontWeight="bold">Indeks</text>
      <text x={M + 54} y={top + 18} fontFamily={FONT} fontSize={6} fontWeight="bold">Data</text>
      <text x={M + 110} y={top + 18} fontFamily={FONT} fontSize={6} fontWeight="bold">Zmiana</text>
      <line x1={M + 2} y1={top + 20} x2={splitX - 2} y2={top + 20} stroke="#000" strokeWidth={0.3} />
      {revs.slice(0, 4).map((r, i) => (
        <g key={r.id ?? 'r' + i}>
          <text x={M + 6} y={top + 28 + i * 8} fontFamily={FONT} fontSize={6}>{nz(r.revIndex)}</text>
          <text x={M + 54} y={top + 28 + i * 8} fontFamily={FONT} fontSize={6}>{fmtDate(r.revDate)}</text>
          <text x={M + 110} y={top + 28 + i * 8} fontFamily={FONT} fontSize={6}>{wrap(nz(r.changeNote), splitX - (M + 110) - 4, 6)[0] ?? ''}</text>
        </g>
      ))}

      {/* prawa: tabliczka tytułowa */}
      {(() => {
        const rx = splitX + 5;
        const rows: Array<[string, string]> = [
          ['', nz(c.companyName)],
          ['Rysunek', nz(p.drawingTitle) || title],
          ['Opracował', nz(p.geologistResolvedName) || nz(p.documentingGeologistText)],
          ['Sprawdził', nz(p.checkedByText)],
          ['Skala', nz(p.depthScale)],
          ['Nr zał.', nz(p.attachmentLabel)],
        ];
        return rows.map(([k, v], i) => (
          <g key={i}>
            {k && <text x={rx} y={top + 9 + i * 9} fontFamily={FONT} fontSize={6} fontWeight="bold">{k}:</text>}
            <text x={rx + (k ? 46 : 0)} y={top + 9 + i * 9} fontFamily={FONT} fontSize={6.2}
              fontWeight={k ? 'normal' : 'bold'}>{(wrap(v, (W - M) - (rx + 48), 6.2)[0] ?? '')}</text>
          </g>
        ));
      })()}
    </g>
  );
}

// ==================== NAGŁÓWEK ====================
function Header({ model, c, p, mapImage, top, mapW, mapH, bottom }: {
  model: BoreholeCardModel;
  c: BoreholeCardModel['context'];
  p: BoreholeCardModel['presentation'];
  mapImage?: string | null;
  top: number; mapW: number; mapH: number; bottom: number;
}) {
  const mapX = M + 2, mapY = top + 2;
  const metaX = mapX + mapW + 8;
  const metaW = (W - M) - metaX - 4;

  // trzeci element krotki (opcjonalny) = `data-field` klikalnego wiersza → pole w edytorze
  const meta: Array<[string, string, string?]> = [
    ['miejscowość', nz(c.town)],
    ['gmina', nz(c.gmina)],
    ['powiat', nz(c.county)],
    ['województwo', nz(c.voivodeship)],
    ['Zamawiający', [nz(c.clientName), nz(c.clientAddress)].filter(Boolean).join(', ')],
    ['Wykonawca', [nz(c.companyName), nz(c.companyAddress),
      c.companyNip ? 'NIP ' + c.companyNip : '', nz(c.companyContact)].filter(Boolean).join(', '), 'pres.company'],
    ['Geolog dokum.', nz(p.geologistResolvedName) || nz(p.documentingGeologistText), 'pres.geologist'],
  ];
  const coords = formatCoordinates(model.latitude, model.longitude, p.coordinateSystem);
  const meta2: Array<[string, string, string?]> = [
    ['Współrzędne', coords, 'borehole.latitude'],
    ['Rzędna', model.elevationMNpm != null ? `${plFixed(model.elevationMNpm, 2)} m n.p.m.` : '', 'borehole.elevationMNpm'],
    ['Czas robót', [fmtDate(model.workStartDate), fmtDate(model.workEndDate)].filter(Boolean).join(' – '), 'borehole.workStartDate'],
    ['System wiercenia', nz(model.drillingSystem), 'borehole.drillingSystem'],
    ['Pobór próbek', nz(model.sampleMethod), 'borehole.sampleMethod'],
    ['Przechow. próbek', nz(model.sampleStorage), 'borehole.sampleStorage'],
  ];

  let ry = top + 12;
  const rowH = 12;
  const renderRow = ([label, value, field]: [string, string, string?]) => {
    const valLines = wrap(value, metaW - 92, 7);
    const h = Math.max(rowH, valLines.length * 8 + 3);
    const el = (
      <g key={label} transform={`translate(${metaX} ${ry})`} data-field={field}>
        {field && <rect x={-2} y={-1} width={metaW + 2} height={h} fill="none" pointerEvents="all" />}
        <text x={0} y={7} fontFamily={FONT} fontSize={7} fontWeight="bold">{label}:</text>
        {valLines.map((ln, i) => (
          <text key={i} x={90} y={7 + i * 8} fontFamily={FONT} fontSize={7}>{ln}</text>
        ))}
      </g>
    );
    ry += h;
    return el;
  };

  return (
    <g>
      {/* mapa */}
      <g data-field="pres.mapScale">
        <rect x={mapX} y={mapY} width={mapW} height={mapH} fill="#f1f5f9" stroke="#000" strokeWidth={0.6} />
        {mapImage
          ? <image x={mapX} y={mapY} width={mapW} height={mapH} href={mapImage} xlinkHref={mapImage} preserveAspectRatio="xMidYMid slice" />
          : <text x={mapX + mapW / 2} y={mapY + mapH / 2} textAnchor="middle" fontFamily={FONT} fontSize={8} fill="#64748b">mapa lokalizacji</text>}
        <text x={mapX + 3} y={mapY + 10} fontFamily={FONT} fontSize={7} fontStyle="italic" fill="#0f172a">
          Lokalizacja otworu w skali {nz(p.mapScale)}
        </text>
      </g>

      {/* pas obliczeń hydrogeologicznych pod mapą — rysowany PRZED metadanymi, aby jego (przezroczysty,
          klikalny) prostokąt nie przechwytywał kliknięć dolnych wierszy metadanych, gdy te się zawijają */}
      <HydroBlock model={model} p={p} x={M + 2} y={mapY + mapH + 4} w={mapW} bottom={bottom} />

      {/* metadane po prawej (na wierzchu — ich klikalne wiersze wygrywają w razie nakładania) */}
      {[...meta, ...meta2].map(renderRow)}
    </g>
  );
}

function HydroBlock({ model, p, x, y, w, bottom }: {
  model: BoreholeCardModel; p: BoreholeCardModel['presentation'];
  x: number; y: number; w: number; bottom: number;
}) {
  const lines: string[] = [];
  const stages = model.pumping.filter((s) => s.qM3h != null || s.drawdownSM != null);
  for (const s of stages) {
    lines.push(
      `pompowanie ${stages.length > 1 ? `st. ${s.stageNo}` : ''}: Q = ${plFixed(s.qM3h, 2)} m³/h, `
      + `s = ${plFixed(s.drawdownSM, 2)} m` + (s.durationH != null ? `, t = ${s.durationH} h` : '')
      + (s.specificCapacity != null ? ` (q = ${plFixed(s.specificCapacity, 2)} m³/h/1m)` : ''),
    );
  }
  if (p.kValueMs != null) {
    lines.push(`k = ${plFixed(p.kValueMs, 6)} m/s` + (p.kFormula ? ` (wzór ${p.kFormula})` : ''));
  }
  if (p.rRangeM != null) {
    lines.push(`zasięg leja depresji R = ${plFixed(p.rRangeM, 1)} m` + (p.rFormula ? ` (wg wzoru ${p.rFormula})` : ''));
  }
  if (p.qExploatacyjneM3h != null || p.qDopFiltraM3h != null) {
    lines.push(
      [p.qExploatacyjneM3h != null ? `Q eksploatacyjne = ${plFixed(p.qExploatacyjneM3h, 2)} m³/h` : '',
       p.qDopFiltraM3h != null ? `Q dop. filtra = ${plFixed(p.qDopFiltraM3h, 2)} m³/h` : ''].filter(Boolean).join('; '),
    );
  }
  if (p.hydroNotes) lines.push(...p.hydroNotes.split('\n'));

  // szerokość na całość pod mapą + trochę w prawo
  const fullW = (W - M) - x - 4;
  return (
    <g data-field="hydro.kValueMs">
      <rect x={x - 2} y={y} width={fullW + 4} height={Math.max(10, bottom - y)} fill="none" pointerEvents="all" />
      <text x={x} y={y + 8} fontFamily={FONT} fontSize={7} fontWeight="bold">
        Wyniki badań i obliczeń hydrogeologicznych:
      </text>
      {lines.flatMap((ln, i) => wrap(ln, fullW, 7).map((w2, j) => ({ w2, key: `${i}-${j}` })))
        .slice(0, Math.max(0, Math.floor((bottom - y - 12) / 8)))
        .map((o, idx) => (
          <text key={o.key} x={x} y={y + 18 + idx * 8} fontFamily={FONT} fontSize={7}>{o.w2}</text>
        ))}
    </g>
  );
}

// ==================== GŁÓWKI KOLUMN ====================
function ColumnHeaders({ cols, x, top, height, panelX }: {
  cols: Array<{ key: string; w: number }>; x: Record<string, number>;
  top: number; height: number; panelX: number;
}) {
  // etykiety krótkie — kolumny wąskie rotowane, szerokie (casing/desc) poziomo z zawijaniem
  const labels: Record<string, string> = {
    depth: 'Głębokość',
    casing: 'Schemat zarurowania i zafiltrowania, sposób zamknięcia wód (rysunek konstrukcyjny)',
    water: 'Poziomy wód nawiercony / ustabilizowany',
    litho: 'Profil litologiczny (graficznie)',
    depthNo: 'Głębokość [m]',
    desc: 'Opis litologiczny warstw, typ facjalny',
    strat: 'Stratygrafia',
    soil: 'Kategoria gruntu',
    tools: 'Narzędzia wiertnicze',
    course: 'Przebieg robót wiertniczych',
    results: 'Inne badania hydrogeologiczne i specjalne — wyniki; wskaźniki fizyko-chemiczne wody',
    uwagi: 'Inne uwagi i zalecenia',
    difficulties: 'Utrudnienia wiertnicze, ucieczki płuczki, zaciskania',
    plannedTests: 'Przewidywane pomiary, badania, próby',
    mud: 'Rodzaj projekt. płuczki',
    bit: 'Rodzaj świdra / rdzeniówki',
    pressure: 'Nacisk [t]',
    rpm: 'Obroty świdra',
    flow: 'Ilość płuczki [l/s]',
    casingExisting: 'Stan istniejący (konstrukcja otworu)',
    casingPlanned: 'Projekt likwidacji / rekonstrukcji',
  };
  const wideKeys = new Set(['casing', 'desc', 'results', 'plannedTests', 'casingExisting', 'casingPlanned']);
  // klik w główkę kolumny → sekcja edytora odpowiadająca za tę kolumnę
  const target: Record<string, string> = {
    depth: 'borehole.totalDepth', casing: 'casing', casingExisting: 'casing', casingPlanned: 'casing',
    water: 'waterlevels', litho: 'layers', depthNo: 'layers', desc: 'layers', strat: 'layers', soil: 'layers',
    tools: 'borehole.drillingTools', course: 'borehole.drillingCourse', results: 'water', uwagi: 'projekt.supervisionNotes',
    difficulties: 'projekt', plannedTests: 'projekt.plannedTests', mud: 'projekt.mudType', bit: 'projekt.bitType',
    pressure: 'projekt.drillPressure', rpm: 'projekt.drillRpm', flow: 'projekt.mudFlow',
  };
  return (
    <g>
      <line x1={M} y1={top} x2={panelX} y2={top} stroke="#000" strokeWidth={0.6} />
      <line x1={M} y1={top + height} x2={W - M} y2={top + height} stroke="#000" strokeWidth={0.8} />
      {cols.map((col, idx) => {
        const cxc = x[col.key] + col.w / 2;
        const label = labels[col.key] ?? col.key;
        const numY = top + height - 2;
        const bandH = height - 10;            // obszar na tekst główki (nad numerem)
        const midY = top + 2 + bandH / 2;
        const field = target[col.key];
        if (wideKeys.has(col.key)) {
          // szeroka kolumna — tekst poziomy, zawijany, przycięty do wysokości pasma
          const lines = wrap(label, col.w - 6, 6).slice(0, Math.max(1, Math.floor(bandH / 7)));
          return (
            <g key={col.key} data-field={field}>
              <rect x={x[col.key]} y={top} width={col.w} height={height} fill="none" pointerEvents="all" />
              {lines.map((ln, i) => (
                <text key={i} x={cxc} y={midY - (lines.length - 1) * 3.5 + i * 7} textAnchor="middle"
                  fontFamily={FONT} fontSize={6}>{ln}</text>
              ))}
              <text x={cxc} y={numY} textAnchor="middle" fontFamily={FONT} fontSize={6} fontWeight="bold" fill="#334155">{idx + 1}</text>
            </g>
          );
        }
        // wąska kolumna — etykieta obrócona, zawinięta na kilka linii mieszczących się w pasmie (nie wychodzą poza obramowanie)
        const lines = wrap(label, bandH, 6);
        const n = lines.length;
        const gap = Math.min(6.5, (col.w - 3) / n);
        return (
          <g key={col.key} data-field={field}>
            <rect x={x[col.key]} y={top} width={col.w} height={height} fill="none" pointerEvents="all" />
            {lines.map((ln, i) => {
              const lx = cxc + (i - (n - 1) / 2) * gap;
              return (
                <text key={i} x={lx} y={midY} textAnchor="middle" fontFamily={FONT} fontSize={6}
                  transform={`rotate(-90 ${lx} ${midY})`}>{ln}</text>
              );
            })}
            <text x={cxc} y={numY} textAnchor="middle" fontFamily={FONT} fontSize={6} fontWeight="bold" fill="#334155">{idx + 1}</text>
          </g>
        );
      })}
    </g>
  );
}

// ==================== KOL.1 SKALA GŁĘBOKOŚCI ====================
// Podziałka adaptacyjna: drobne kreski co `minor`, opisane co `major` — dokładniejsza „linijka"
// jak na profesjonalnych drukach (1:250). Wartości mogą być dziesiętne (granice warstw z przecinkiem).
function DepthAxis({ x, w, maxDepth, yAt }: {
  x: number; w: number; maxDepth: number; yAt: (d: number) => number;
}) {
  const [minor, major] =
    maxDepth <= 60 ? [1, 5] :
    maxDepth <= 120 ? [2, 10] :
    [5, 20];
  const n = Math.floor(maxDepth / minor + 1e-6);
  const ticks: number[] = [];
  for (let i = 0; i <= n; i++) ticks.push(Number((i * minor).toFixed(3)));
  const isMajor = (d: number) => Math.abs(d / major - Math.round(d / major)) < 1e-6;
  return (
    <g data-field="borehole.totalDepth">
      {ticks.map((d) => {
        const maj = isMajor(d);
        const yy = yAt(d);
        return (
          <g key={d}>
            <line x1={maj ? x + w - 6 : x + w - 3} y1={yy} x2={x + w} y2={yy}
              stroke="#000" strokeWidth={maj ? 0.5 : 0.3} />
            {maj && (
              <text x={x + w - 7} y={yy + 2} textAnchor="end" fontFamily={FONT} fontSize={6}>{pl(d, 0)}</text>
            )}
          </g>
        );
      })}
    </g>
  );
}

// ==================== WARSTWA (litologia + głęb. + opis + kat.) ====================
function LayerRow({ l, yAt, xLitho, wLitho, xDepthNo, wDepthNo, xDesc, wDesc, xSoil, wSoil }: {
  l: IdxLayer; yAt: (d: number) => number;
  xLitho: number; wLitho: number; xDepthNo: number; wDepthNo: number;
  xDesc: number; wDesc: number; xSoil: number; wSoil: number;
}) {
  const y1 = yAt(l.fromDepth ?? 0), y2 = yAt(l.toDepth ?? 0);
  const h = Math.max(0.5, y2 - y1);
  const midY = (y1 + y2) / 2;
  const color = l.color || '#cbd5e1';
  const desc = [nz(l.description), nz(l.facialType)].filter(Boolean).join(', ');
  const descLines = wrap(desc, wDesc - 6, 6.5);
  return (
    <g data-field={`layer.${l._idx}.desc`}>
      {/* przezroczysty obszar klikania: litologia + opis */}
      <rect x={xLitho} y={y1} width={Math.max(0, xDesc + wDesc - xLitho)} height={h} fill="none" pointerEvents="all" />
      {/* litologia graficzna */}
      <rect x={xLitho} y={y1} width={wLitho} height={h} fill={color} />
      <rect x={xLitho} y={y1} width={wLitho} height={h} fill={lithoFill(l.pattern)} />
      <line x1={xLitho} y1={y2} x2={xLitho + wLitho} y2={y2} stroke="#000" strokeWidth={0.4} />
      {/* granica głębokości */}
      <line x1={xDepthNo} y1={y2} x2={xDepthNo + wDepthNo} y2={y2} stroke="#000" strokeWidth={0.4} />
      <text x={xDepthNo + wDepthNo - 3} y={y2 - 1.5} textAnchor="end" fontFamily={FONT} fontSize={6}>{plFixed(l.toDepth, 1)}</text>
      {/* opis */}
      {descLines.map((ln, i) => (
        <text key={i} x={xDesc + 3} y={midY - (descLines.length - 1) * 3.5 + i * 7 + 2} fontFamily={FONT} fontSize={6.5}>{ln}</text>
      ))}
      <line x1={xDesc} y1={y2} x2={xDesc + wDesc} y2={y2} stroke="#000" strokeWidth={0.3} />
      {/* kategoria gruntu */}
      {xSoil >= 0 && nz(l.soilCategory) && (
        <text x={xSoil + wSoil / 2} y={midY + 2} textAnchor="middle" fontFamily={FONT} fontSize={6}>{l.soilCategory}</text>
      )}
    </g>
  );
}

// ==================== KOL.7 STRATYGRAFIA (scalanie) ====================
function StratigraphyColumn({ layers, yAt, x, w }: {
  layers: IdxLayer[]; yAt: (d: number) => number; x: number; w: number;
}) {
  const groups: Array<{ from: number; to: number; text: string; idx: number }> = [];
  for (const l of layers) {
    const t = nz(l.stratigraphy);
    if (!t) continue;
    const last = groups[groups.length - 1];
    if (last && last.text === t && Math.abs(last.to - (l.fromDepth ?? 0)) < 0.001) {
      last.to = l.toDepth ?? last.to;
    } else {
      groups.push({ from: l.fromDepth ?? 0, to: l.toDepth ?? 0, text: t, idx: l._idx });
    }
  }
  return (
    <g>
      {groups.map((g, i) => {
        const y1 = yAt(g.from), y2 = yAt(g.to);
        const cyc = (y1 + y2) / 2, cxc = x + w / 2;
        // czcionka dopasowana do wysokości warstwy, by nazwa (np. „Czwartorzęd — plejstocen") się zmieściła
        const fs = Math.max(4.6, Math.min(8, (y2 - y1 - 4) / Math.max(1, g.text.length * 0.55)));
        return (
          <g key={i} data-field={`layer.${g.idx}.strat`}>
            <rect x={x} y={y1} width={w} height={Math.max(0.5, y2 - y1)} fill="none" pointerEvents="all" />
            <line x1={x} y1={y2} x2={x + w} y2={y2} stroke="#000" strokeWidth={0.3} />
            <text x={cxc} y={cyc} textAnchor="middle" fontFamily={FONT} fontSize={fs} fontWeight="bold"
              transform={`rotate(-90 ${cxc} ${cyc})`}>{g.text}</text>
          </g>
        );
      })}
    </g>
  );
}

// ==================== KOL.2 KONSTRUKCJA ====================
const DECISION_LABEL: Record<string, string> = {
  REMOVE: 'wyciągnąć', LEAVE: 'pozostawić', LEAVE_BROKEN: 'pozostawić (zerwane)', ATTEMPT_REMOVE: 'próba wyciągnięcia',
};
// Nazwa opisowa elementu konstrukcji (zgodna z etykietami edytora) — do wykładek przy rysunku.
const CASING_KIND_LABEL: Record<string, string> = {
  DRILL_BORE: 'Przewiert', CASING_PIPE: 'Rura okładzinowa', OVER_FILTER_PIPE: 'Rura nadfiltrowa',
  FILTER: 'Filtr szczelinowy', UNDER_FILTER_PIPE: 'Rura podfiltrowa', GRAVEL_PACK: 'Obsypka żwirowa',
  CLAY_PLUG: 'Korek iłowy', SEAL: 'Uszczelnienie', CEMENTATION: 'Cementacja',
  SUMP: 'Rura osadnikowa', BOTTOM_CAP: 'Denko',
};
// Elementy „obszarowe" (przestrzeń pierścieniowa) — przy nich podajemy zakres głębokości.
const AREA_KINDS = new Set(['GRAVEL_PACK', 'CLAY_PLUG', 'SEAL', 'CEMENTATION']);

/** Pełny opis segmentu: nazwa + materiał + Ø + szczelina/frakcja + (zakres) + decyzja. */
function casingLabelText(s: IdxCasing, showDecision?: boolean): string {
  const head = CASING_KIND_LABEL[s.kind] || nz(s.label);
  const spec: string[] = [];
  if (nz(s.material)) spec.push(s.material as string);
  if (nz(s.diameterInch)) spec.push(`Ø ${s.diameterInch}`);
  else if (s.outerDiameterMm != null) spec.push(`Ø ${pl(s.outerDiameterMm, 0)} mm`);
  if (nz(s.meshType)) spec.push(s.meshType as string);
  if (nz(s.fraction)) spec.push(`fr. ${s.fraction}`);
  if (AREA_KINDS.has(s.kind) && s.fromDepth != null && s.toDepth != null)
    spec.push(`(${plFixed(s.fromDepth, 1)}–${plFixed(s.toDepth, 1)} m)`);
  const dec = showDecision && s.decision ? ` (${DECISION_LABEL[s.decision] ?? ''})` : '';
  return [head, spec.join(', ')].filter(Boolean).join(' ') + dec;
}

function CasingColumn({ casing, yAt, x, w, showDecision, stickup = 0 }: {
  casing: IdxCasing[]; yAt: (d: number) => number; x: number; w: number; showDecision?: boolean; stickup?: number;
}) {
  const segs = casing.filter((s) => s.fromDepth != null || s.toDepth != null);
  const mainSegs = segs.filter((s) => !s.piezometer);
  const piezoSegs = segs.filter((s) => s.piezometer);

  // układ: [lewa rynna rzędnych] [rysunek] [klamry wymiarowe] [wykładki opisowe]
  const wide = w >= 70;
  const showDims = w >= 88;                       // klamry wymiarowe tylko gdy jest miejsce
  const LM = wide ? 13 : 0;                       // rynna rzędnych po lewej
  const drawMax = wide ? 26 : Math.min(28, w * 0.5);
  const dimLane = showDims ? 10 : 0;
  const drawX0 = x + LM;
  const center = drawX0 + drawMax / 2;
  const dimX = drawX0 + drawMax + 3;             // oś klamr wymiarowych
  const labelX = drawX0 + drawMax + dimLane + 4;
  const labelW = Math.max(20, x + w - labelX - 2);
  const maxD = Math.max(1, ...mainSegs.map((s) => s.outerDiameterMm ?? 0));
  const pxPerMm = maxD > 0 ? (drawMax - 4) / maxD : 0;
  const segW = (d: number | null) => Math.max(4, (d ?? maxD * 0.4) * pxPerMm);

  const styleFor = (kind: string): { fill: string; overlay?: string } => {
    switch (kind) {
      case 'FILTER': return { fill: 'none', overlay: lithoFill('HORIZONTAL_LINES') };
      case 'GRAVEL_PACK': return { fill: 'none', overlay: lithoFill('DOTS') };
      case 'CLAY_PLUG': return { fill: '#a16207' };
      case 'SEAL': return { fill: '#334155' };
      case 'CEMENTATION': return { fill: '#94a3b8' };
      default: return { fill: 'none' };
    }
  };

  // rzędne granic segmentów (lewa rynna)
  const bounds = Array.from(new Set(
    mainSegs.flatMap((s) => [s.fromDepth, s.toDepth]).filter((d): d is number => d != null),
  )).sort((a, b) => a - b);

  // wystawka rury ponad terenem: rury zaczynające się przy 0 „wystają" o stickPx nad linią „0,0"
  const y0 = yAt(0);
  const stickPx = 11;
  const risers = stickup > 0 ? mainSegs.filter((s) => (s.fromDepth ?? 0) <= 0.05) : [];

  // klamry wymiarowe długości elementów kolumny (nadfiltrowa/filtr/podfiltrowa/osadnik)
  const DIM_KINDS = new Set(['OVER_FILTER_PIPE', 'FILTER', 'UNDER_FILTER_PIPE', 'SUMP']);
  const dims = showDims
    ? mainSegs.filter((s) => DIM_KINDS.has(s.kind) && s.fromDepth != null && s.toDepth != null)
    : [];

  // wykładki opisowe: układane pionowo z odpychaniem (nie nachodzą), z linią odniesienia
  let prevBottom = -Infinity;
  const labelBlocks = mainSegs
    .slice()
    .sort((a, b) => (a.fromDepth ?? 0) - (b.fromDepth ?? 0))
    .map((s) => {
      const y1 = yAt(s.fromDepth ?? 0), y2 = yAt(s.toDepth ?? (s.fromDepth ?? 0));
      const midY = (y1 + y2) / 2;
      const lines = wrap(casingLabelText(s, showDecision), labelW, 5.5);
      const blockH = lines.length * 6;
      const ly = Math.max(midY - blockH / 2, prevBottom + 2);
      prevBottom = ly + blockH;
      return { s, midY, lines, ly, blockH };
    });

  const piezoX = drawX0 + drawMax - 4;
  return (
    <g>
      {/* rzędne głębokości po lewej stronie rysunku */}
      {wide && bounds.map((d) => (
        <g key={'b' + d}>
          <line x1={drawX0 - 3} y1={yAt(d)} x2={drawX0} y2={yAt(d)} stroke="#000" strokeWidth={0.3} />
          <text x={drawX0 - 4} y={yAt(d) + 2} textAnchor="end" fontFamily={FONT} fontSize={4.6} fill="#334155">{plFixed(d, 1)}</text>
        </g>
      ))}
      {/* rysunek segmentów */}
      {mainSegs.map((s, i) => {
        const y1 = yAt(s.fromDepth ?? 0), y2 = yAt(s.toDepth ?? (s.fromDepth ?? 0));
        const hh = Math.max(1, y2 - y1);
        const sw = segW(s.outerDiameterMm);
        const st = styleFor(s.kind);
        return (
          <g key={s.id ?? 'c' + i} data-field={`casing.${s._idx}.from`}>
            <rect x={x} y={y1} width={w} height={hh} fill="none" pointerEvents="all" />
            <rect x={center - sw / 2} y={y1} width={sw} height={hh} fill={st.fill} stroke="#000" strokeWidth={0.5} />
            {st.overlay && <rect x={center - sw / 2} y={y1} width={sw} height={hh} fill={st.overlay} />}
          </g>
        );
      })}
      {/* wystawka rury ponad terenem („+0,5 m") + linia terenu */}
      {stickup > 0 && (
        <g>
          {risers.map((s, i) => {
            const sw = segW(s.outerDiameterMm);
            const st = styleFor(s.kind);
            return (
              <g key={'rz' + i}>
                <rect x={center - sw / 2} y={y0 - stickPx} width={sw} height={stickPx} fill={st.fill} stroke="#000" strokeWidth={0.5} />
                {st.overlay && <rect x={center - sw / 2} y={y0 - stickPx} width={sw} height={stickPx} fill={st.overlay} />}
              </g>
            );
          })}
          <line x1={drawX0 - 2} y1={y0} x2={drawX0 + drawMax + 2} y2={y0} stroke="#000" strokeWidth={0.5} strokeDasharray="3 1.5" />
          <text x={center} y={y0 - stickPx - 2} textAnchor="middle" fontFamily={FONT} fontSize={5} fill="#334155">+{plFixed(stickup, 1)} m</text>
        </g>
      )}
      {/* klamry wymiarowe długości elementów */}
      {dims.map((s, i) => {
        const y1 = yAt(s.fromDepth as number), y2 = yAt(s.toDepth as number);
        const midY = (y1 + y2) / 2;
        const len = (s.toDepth as number) - (s.fromDepth as number) + ((s.fromDepth as number) <= 0.05 ? stickup : 0);
        return (
          <g key={'dim' + i}>
            <line x1={dimX} y1={y1} x2={dimX} y2={y2} stroke="#475569" strokeWidth={0.4} />
            <line x1={dimX - 2} y1={y1} x2={dimX + 2} y2={y1} stroke="#475569" strokeWidth={0.4} />
            <line x1={dimX - 2} y1={y2} x2={dimX + 2} y2={y2} stroke="#475569" strokeWidth={0.4} />
            <text x={dimX + 3} y={midY} textAnchor="middle" fontFamily={FONT} fontSize={5} fill="#475569"
              transform={`rotate(-90 ${dimX + 3} ${midY})`}>{plFixed(len, 1)} m</text>
          </g>
        );
      })}
      {/* wykładki opisowe z liniami odniesienia */}
      {labelBlocks.map(({ s, midY, lines, ly, blockH }, i) => (
        <g key={'lab' + i} data-field={`casing.${s._idx}.from`}>
          <line x1={labelX - 2} y1={ly + blockH / 2} x2={drawX0 + drawMax + dimLane} y2={midY} stroke="#94a3b8" strokeWidth={0.3} />
          {lines.map((ln, j) => (
            <text key={j} x={labelX} y={ly + 5 + j * 6} fontFamily={FONT} fontSize={5.5}>{ln}</text>
          ))}
        </g>
      ))}
      {/* piezometr — cienki równoległy słupek z boku (niebieski) */}
      {piezoSegs.map((s, i) => {
        const y1 = yAt(s.fromDepth ?? 0), y2 = yAt(s.toDepth ?? (s.fromDepth ?? 0));
        return (
          <rect key={s.id ?? 'pz' + i} x={piezoX - 2.5} y={y1} width={5} height={Math.max(1, y2 - y1)}
            fill={s.kind === 'FILTER' ? lithoFill('HORIZONTAL_LINES') : 'none'} stroke="#0369a1" strokeWidth={0.6}
            data-field={`casing.${s._idx}.from`} />
        );
      })}
      {piezoSegs.length > 0 && (
        <text x={piezoX} y={yAt(piezoSegs[0].fromDepth ?? 0) + 2} fontFamily={FONT} fontSize={5} fill="#0369a1"
          transform={`rotate(-90 ${piezoX} ${yAt(piezoSegs[0].fromDepth ?? 0) + 2})`}>Piezometr</text>
      )}
    </g>
  );
}

// ==================== LIKWIDACJA: STREFY WYPEŁNIEŃ ====================
const LIQ_STYLE: Record<string, { fill: string; overlay?: string; label: string }> = {
  CEMENT_PLUG: { fill: '#94a3b8', label: 'korek cementowy' },
  CONCRETE_PLUG: { fill: '#9ca3af', label: 'korek betonowy' },
  BENTONITE: { fill: '#b08d57', overlay: lithoFill('HORIZONTAL_LINES'), label: 'bentonit' },
  COMPACTONITE: { fill: '#b08d57', overlay: lithoFill('HORIZONTAL_LINES'), label: 'compactonit' },
  CLAY_SEAL: { fill: '#b45309', overlay: lithoFill('DIAGONAL_LINES'), label: 'iłowanie' },
  GRAVEL_FILL: { fill: '#fde68a', overlay: lithoFill('CIRCLES'), label: 'żwir' },
  SAND_BACKFILL: { fill: '#fef3c7', overlay: lithoFill('DOTS'), label: 'samozasyp / piasek' },
  MARKER_PLATE: { fill: '#e2e8f0', label: 'świadek / płyta' },
};
function LiquidationColumn({ zones, yAt, x, w }: {
  zones: IdxLiq[]; yAt: (d: number) => number; x: number; w: number;
}) {
  const zs = zones.filter((z) => z.material && (z.fromDepth != null || z.toDepth != null));
  const bandX = x + 4, bandW = w * 0.4;
  return (
    <g>
      {zs.map((z, i) => {
        const y1 = yAt(z.fromDepth ?? 0), y2 = yAt(z.toDepth ?? (z.fromDepth ?? 0));
        const hh = Math.max(1, y2 - y1);
        const st = LIQ_STYLE[z.material as string] ?? { fill: '#e5e7eb', label: nz(z.material) };
        const lbl = st.label + (z.disinfectant ? ' + chlorowanie' : '');
        return (
          <g key={z.id ?? 'z' + i} data-field={`liq.${z._idx}.from`}>
            <rect x={x} y={y1} width={w} height={hh} fill="none" pointerEvents="all" />
            <rect x={bandX} y={y1} width={bandW} height={hh} fill={st.fill} stroke="#000" strokeWidth={0.4} />
            {st.overlay && <rect x={bandX} y={y1} width={bandW} height={hh} fill={st.overlay} />}
            <text x={bandX + bandW + 3} y={(y1 + y2) / 2 + 2} fontFamily={FONT} fontSize={5.5}>{lbl}</text>
            {z.note && <text x={bandX + bandW + 3} y={(y1 + y2) / 2 + 9} fontFamily={FONT} fontSize={5} fill="#475569">{z.note.slice(0, 44)}</text>}
          </g>
        );
      })}
    </g>
  );
}

// ==================== KOL.3 ZWIERCIADŁO WODY ====================
// Zwierciadło nawiercone (DRILLED, trójkąt pusty) i ustabilizowane (STABILIZED, trójkąt pełny)
// rysujemy w osobnych slotach (lewy/prawy), żeby się NIE NACHODZIŁY; gdy są na różnych
// poziomach — łączymy je skośną linią (konwencja hydrogeologiczna).
function WaterLevels({ levels, yAt, x, w }: {
  levels: IdxLevel[]; yAt: (d: number) => number; x: number; w: number;
}) {
  const cxc = x + w / 2;
  const COL = '#0369a1';
  const pts = levels
    .filter((lv) => lv.depthMPpt != null)
    .map((lv) => ({ ...lv, y: yAt(lv.depthMPpt as number) }));
  if (pts.length === 0) return null;

  const drilled = pts.filter((p) => p.kind === 'DRILLED');
  const stab = pts.filter((p) => p.kind !== 'DRILLED');   // ustabilizowane (i ewentualne inne)
  const both = drilled.length > 0 && stab.length > 0;
  const dx = Math.min(6, Math.max(3, w * 0.22));           // odsunięcie slotu od osi
  const slotX = (p: (typeof pts)[number]) => !both ? cxc : (p.kind === 'DRILLED' ? cxc - dx : cxc + dx);
  const triH = 5.5, triW = 3;

  return (
    <g>
      {/* skośna linia łącząca poziom nawiercony z ustabilizowanym (tylko gdy różne głębokości) */}
      {both && drilled.length === 1 && stab.length === 1 && Math.abs(drilled[0].y - stab[0].y) > 0.5 && (
        <line x1={cxc - dx} y1={drilled[0].y} x2={cxc + dx} y2={stab[0].y} stroke={COL} strokeWidth={0.6} />
      )}
      {pts.map((p, i) => {
        const cx = slotX(p);
        const filled = p.kind !== 'DRILLED';                // ustabilizowane = wypełniony
        return (
          <g key={p.id ?? 'w' + i} data-field={`waterlevel.${p._idx}.depth`}>
            <rect x={cx - dx - 2} y={p.y - triH - 1} width={dx * 2 + 4} height={triH + 10} fill="none" pointerEvents="all" />
            <polygon points={`${cx - triW},${p.y - triH} ${cx + triW},${p.y - triH} ${cx},${p.y}`}
              fill={filled ? COL : '#fff'} stroke={COL} strokeWidth={0.8} />
            <line x1={cx - triW - 1.5} y1={p.y} x2={cx + triW + 1.5} y2={p.y} stroke={COL} strokeWidth={0.8} />
            <text x={cx} y={p.y + 7} textAnchor="middle" fontFamily={FONT} fontSize={5} fill={COL}>{plFixed(p.depthMPpt, 1)}</text>
          </g>
        );
      })}
    </g>
  );
}

// ==================== TEKST PIONOWY ====================
function VerticalText({ text, x, yTop, yBot, fontSize }: {
  text: string; x: number; yTop: number; yBot: number; fontSize: number;
}) {
  if (!text) return null;
  const h = yBot - yTop;
  const lines = wrap(text, h, fontSize);
  const n = lines.length;
  return (
    <g>
      {lines.map((ln, i) => {
        const lx = x - (n - 1) * 4 + i * 8;
        return (
          <text key={i} x={lx} y={yTop} fontFamily={FONT} fontSize={fontSize}
            transform={`rotate(90 ${lx} ${yTop})`}>{ln}</text>
        );
      })}
    </g>
  );
}

// ==================== ZZWW: WYNIKI WODY W KOLUMNIE 11 (lista numerowana) ====================
function WaterColumn({ model, x, w, top, bottom }: {
  model: BoreholeCardModel; x: number; w: number; top: number; bottom: number;
}) {
  const water = model.water;
  const px = x + 3;
  const items: Array<{ t: string; y: number; b: boolean }> = [];
  let y = top + 9;
  const push = (t: string, size: number, bold: boolean) => {
    for (const ln of wrap(t, w - 6, size)) {
      if (y > bottom - 3) return;
      items.push({ t: ln, y, b: bold });
      y += size + 1.4;
    }
  };
  if (water?.sampledDate) push(`Wyniki badania wody z dn. ${fmtDate(water.sampledDate)}`, 6.5, true);
  if (water?.labName) push(`Lab.: ${water.labName}`, 6.3, false);
  y += 2;
  (water?.values ?? []).forEach((v, i) => {
    push(`${i + 1}. ${nz(v.paramName)}: ${nz(v.valueText) || '–'}${v.unit ? ' ' + v.unit : ''}`, 6.3, false);
  });
  return (
    <g data-field="water.sampledDate">
      <rect x={x} y={top} width={w} height={Math.max(0, bottom - top)} fill="none" pointerEvents="all" />
      {items.map((it, i) => (
        <text key={i} x={px} y={it.y} fontFamily={FONT} fontSize={it.b ? 6.5 : 6.3} fontWeight={it.b ? 'bold' : 'normal'}>{it.t}</text>
      ))}
    </g>
  );
}

// ==================== PRAWY PANEL: WYNIKI BADANIA WODY ====================
// Panel sąsiaduje z profilem, więc dzieli z nim poziome kreski: `top` = góra pasa główek kolumn,
// `bodyTop` = kreska zamykająca główki (PROF_TOP). Tytuł siedzi w pasie główek (jak etykieta kolumny),
// a metryczka i tabela zaczynają się DOPIERO pod kreską — inaczej przecinała ona wiersze wyników.
function WaterPanel({ model, x, right, top, bodyTop, bottom }: {
  model: BoreholeCardModel; x: number; right: number; top: number; bodyTop: number; bottom: number;
}) {
  const water = model.water;
  const px = x + 4;
  const pw = right - x - 8;
  const cVal = right - 6;         // kolumna wartości (prawa)
  const cUnit = right - 78;       // kolumna jednostki
  const headY = top + (bodyTop - top) / 2 + 3;   // tytuł wyśrodkowany w pasie główek

  // pozycje liczone z góry na dół (bez mutacji w JSX), żeby układ nie zależał od kolejności renderu
  const meta: Array<{ t: string; y: number }> = [];
  let y = bodyTop;
  if (water?.sampledDate) { y += 11; meta.push({ t: `Data poboru: ${fmtDate(water.sampledDate)}`, y }); }
  if (water?.labName) { y += 9; meta.push({ t: `Badający: ${water.labName}`, y }); }
  const headRowY = y + (meta.length ? 12 : 13);   // nagłówek tabeli parametrów

  return (
    <g>
      <g data-field="water.sampledDate">
        <rect x={x} y={top} width={right - x} height={Math.max(0, bodyTop - top)} fill="none" pointerEvents="all" />
        <text x={x + (right - x) / 2} y={headY} textAnchor="middle" fontFamily={FONT} fontSize={9} fontWeight="bold">
          WYNIKI BADANIA WODY
        </text>
      </g>
      {meta.map((m, i) => (
        <text key={'m' + i} x={px} y={m.y} fontFamily={FONT} fontSize={6.5}>{m.t}</text>
      ))}
      {/* nagłówek tabeli */}
      <g>
        <text x={px} y={headRowY} fontFamily={FONT} fontSize={6.5} fontWeight="bold">PARAMETR</text>
        <text x={cUnit} y={headRowY} fontFamily={FONT} fontSize={6.5} fontWeight="bold">JEDN.</text>
        <text x={cVal} y={headRowY} textAnchor="end" fontFamily={FONT} fontSize={6.5} fontWeight="bold">WART.</text>
        <line x1={px} y1={headRowY + 2} x2={cVal} y2={headRowY + 2} stroke="#000" strokeWidth={0.4} />
      </g>
      {/* wiersze parametrów */}
      {(water?.values ?? []).map((v, i) => {
        const ry = headRowY + 9 + i * 8.4;
        if (ry > bottom - 4) return null;
        return (
          <g key={v.id ?? 'v' + i} data-field={`waterval.${i}.name`}>
            <rect x={px - 2} y={ry - 7} width={pw + 4} height={8.4} fill="none" pointerEvents="all" />
            <text x={px} y={ry} fontFamily={FONT} fontSize={6.3}>{nz(v.paramName)}</text>
            <text x={cUnit} y={ry} fontFamily={FONT} fontSize={5.8} fill="#475569">{nz(v.unit)}</text>
            <text x={cVal} y={ry} textAnchor="end" fontFamily={FONT} fontSize={6.3}>{nz(v.valueText) || '–'}</text>
          </g>
        );
      })}
    </g>
  );
}
